import { useState, useMemo, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import { Search, Package, ArrowLeft, AlertTriangle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { industriesData } from "@/data/industriesData";
import { industryTranslations } from "@/data/industriesTranslations";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { fetchProducts } from "@/data/productsData";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from 'use-debounce';
import { Helmet } from "react-helmet-async";

const ITEMS_PER_PAGE = 6;

const IndustryDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, t, getLocalizedPath } = useLanguage();

  const industry = useMemo(() => {
    const industryBase = industriesData.find((ind) => ind.slug === slug);
    if (!industryBase) return null;

    const translation = industryTranslations[industryBase.slug]?.[language] || industryTranslations[industryBase.slug]?.en;
    return {
      ...industryBase,
      ...translation,
    };
  }, [slug, language]);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  
    // Primary query: Fetch products filtered by industry type
    const { 
      data: industryProductsData, 
      isLoading: isLoadingIndustryProducts, 
      isError: isErrorIndustryProducts,
      isSuccess: isSuccessIndustryProducts,
    } = useQuery({
      queryKey: ['industryProducts', industry?.slug, language, currentPage, debouncedSearchQuery],
      queryFn: () => fetchProducts({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        type: industry?.title,
        search: debouncedSearchQuery,
      }),
      enabled: !!industry,
    });
  
    // Fallback query: Fetch any products if the primary query finds none
    const showFallback = isSuccessIndustryProducts && industryProductsData.totalProducts === 0 && !debouncedSearchQuery;
    
    const { 
      data: fallbackProductsData, 
      isLoading: isLoadingFallback, 
      isError: isErrorFallback,
    } = useQuery({
      queryKey: ['industryFallbackProducts', 'random'], // Simplified key for random data
      queryFn: () => fetchProducts({
        limit: ITEMS_PER_PAGE,
        random: true, // Fetch random products
      }),
      enabled: showFallback, // Enable only when primary query is successful, empty, and no search is active
    });
  
    const isLoadingProducts = isLoadingIndustryProducts || (showFallback && isLoadingFallback);
    const isErrorProducts = isErrorIndustryProducts || (showFallback && isErrorFallback);
    const data = showFallback ? fallbackProductsData : industryProductsData;
    
    const paginatedProducts = data?.products || [];
    const totalProducts = data?.totalProducts || 0;
    const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);
  
    useEffect(() => {
      setCurrentPage(1);
    }, [debouncedSearchQuery, setCurrentPage]);
  
    if (!industry) return <Navigate to={getLocalizedPath("industries")} replace />;

  const { title, description, products, tasks, solutions, suppliers, advantages, icon: Icon } = industry;

  const pageTitle = `${title} | ${t('industries.title')} | KM KIT`;
  const pageDescription = description;
  const pageKeywords = [...products, ...tasks, ...solutions, ...suppliers.flatMap(s => s.brands.split(', ').map(brand => brand.trim()))].join(', ');


  const renderProductGrid = () => {
    if (isLoadingProducts) {
      return Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
        <Card key={index}>
          <CardContent className="p-6">
            <Skeleton className="h-48 w-full mb-4" />
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/4 mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-full mb-4" />
            <Skeleton className="h-6 w-1/4" />
          </CardContent>
          <CardFooter className="p-6 pt-0">
            <Skeleton className="h-10 w-full" />
          </CardFooter>
        </Card>
      ));
    }

    if (isErrorProducts) {
      return (
        <div className="text-center py-12 col-span-full">
          <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">{t('products.loadingError')}</h3>
          <p className="text-muted-foreground">{t('products.errorMessage')}</p>
        </div>
      );
    }

    if (paginatedProducts.length > 0) {
      return paginatedProducts.map((product) => {
        const productName = product['name_' + language] || product.name_source || t('product.comingSoon');
        return (
          <Card key={product.id} className="border transition-shadow hover:shadow-md">
            <CardContent className="p-6">
              <div className="bg-secondary rounded-lg h-48 flex items-center justify-center mb-4">
                {product.image ? (
                  <img src={product.image} alt={productName} loading="lazy" className="h-full w-full object-cover rounded-lg" />
                ) : (
                  <Package className="h-16 w-16 text-muted-foreground" />
                )}
              </div>
              <h3 className="text-xl font-semibold mb-2">{productName}</h3>
              <div className="text-sm text-primary font-medium mb-2">
                {product.manufacturer}
              </div>
              <p className="text-sm text-muted-foreground mb-4 h-20 overflow-hidden">
                {product['description_' + language]}
              </p>
              <div className="inline-block px-2 py-1 bg-secondary rounded text-xs font-medium">
                {product.type}
              </div>
            </CardContent>
            <CardFooter className="p-6 pt-0">
              <Button asChild className="w-full bg-accent hover:bg-accent/90">
                <Link to={getLocalizedPath(`/products/${product.id}`)}>{t('products.viewDetails')}</Link>
              </Button>
            </CardFooter>
          </Card>
        );
      });
    }

    return (
      <div className="text-center py-12 col-span-full">
        <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">{t('products.noProducts')}</h3>
        <p className="text-muted-foreground">
          {t('products.noProductsHint')}
        </p>
      </div>
    );
  };

    return (
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="keywords" content={pageKeywords} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://www.kmkit.com${getLocalizedPath(`/industries/${industry?.slug}`)}`} />
        </Helmet>
        <Header />
        <main className="flex-1 py-12">
          <div className="container mx-auto px-4">
            <Link to={getLocalizedPath("industries")} className="inline-flex items-center text-primary hover:text-primary/80 mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />{t("industries.backTo")}
            </Link>
    
            <div className="bg-secondary rounded-lg p-8 md:p-12 mb-12">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">{title}</h1>
                    <p className="text-lg text-muted-foreground">{description}</p>
                  </div>
                </div>
    
                <div className="grid md:grid-cols-2 gap-8 mt-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">{t("industry.typicalProducts")}</h3>
                    <ul className="space-y-2">{products.map((p, i) => <li key={i} className="flex items-start"><span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mt-2 mr-3 flex-shrink-0"></span><span className="text-muted-foreground">{p}</span></li>)}</ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-4">{t("industry.typicalTasks")}</h3>
                    <ul className="space-y-2">{tasks.map((task, i) => <li key={i} className="flex items-start"><span className="inline-block w-1.5 h-1.5 rounded-full bg-accent mt-2 mr-3 flex-shrink-0"></span><span className="text-muted-foreground">{task}</span></li>)}</ul>
                  </div>
                </div>
    
                <div className="mt-8">
                  <h3 className="text-xl font-semibold mb-4">{t("industry.ourSolutions")}</h3>
                  <ul className="space-y-2">{solutions.map((s, i) => <li key={i} className="flex items-start"><span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mt-2 mr-3 flex-shrink-0"></span><span className="text-muted-foreground">{s}</span></li>)}</ul>
                </div>
    
                <div className="mt-8">
                  <h3 className="text-xl font-semibold mb-4">{t("industry.keySuppliers")}</h3>
                  <ul className="space-y-3">{suppliers.map((sup, i) => <li key={i} className="flex items-start gap-3"><span className="font-semibold text-foreground min-w-[140px]">{sup.category}:</span><span className="text-muted-foreground">{sup.brands}</span></li>)}</ul>
                </div>
    
                <div className="mt-8">
                  <h3 className="text-xl font-semibold mb-4">{t("industry.ourAdvantages")}</h3>
                  <div className="grid md:grid-cols-2 gap-3">{advantages.map((adv, i) => <div key={i} className="bg-gradient-to-b from-accent/5 to-accent/10 rounded-lg p-4 border border-accent/10"><span className="text-accent font-semibold mr-2">✓</span><span className="text-muted-foreground">{adv}</span></div>)}</div>
                </div>
    
                <div className="mt-8 flex flex-wrap gap-4">
                  <Button asChild size="lg" className="bg-accent hover:bg-accent/90"><Link to={getLocalizedPath("contacts")}>{t("industry.requestQuote")}</Link></Button>
                  <Button asChild size="lg" variant="outline"><a href="#products-section">{t("industry.openCatalog")}</a></Button>
                </div>
    
                <div className="mt-12 pt-8 border-t border-border">
                  <div className="bg-secondary/30 rounded-lg p-6 text-center">
                    <h3 className="text-lg font-semibold mb-4">{t("industry.contactUs")}</h3>
                    <div className="text-muted-foreground space-y-1">
                      <p className="font-semibold text-foreground">KM KIT mbH</p>
                      <p>Aschmattstraße 8, 76532 Baden-Baden, Germany</p>
                      <p><a href="mailto:info@kmkit.com" className="text-primary hover:underline">info@kmkit.com</a></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
    
            <div id="products-section">
              <div className="mb-8">
                <h2 className="text-3xl font-bold mb-4">{t("industry.productsFor")} {title}</h2>
                <p className="text-lg text-muted-foreground">{t("industry.productsSubtitle")}</p>
              </div>
              {showFallback && (
                <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4 text-sm">
                  {t("industry.fallbackMessage")}
                </div>
              )}
              <div className="mb-8 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input placeholder={t("products.searchPlaceholder")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" disabled={isLoadingProducts || isErrorProducts} />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[400px]">{renderProductGrid()}</div>
            <div className="mt-12 flex flex-col items-center gap-4">
              {/* Page info */}
              <p className="text-sm text-muted-foreground">
                {t('products.page')} {currentPage} {t('products.of')} {totalPages}
              </p>
              
              <Pagination>
                <PaginationContent className="flex-wrap justify-center gap-1">
                  {/* Previous button */}
                  <PaginationItem>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="h-9 px-3"
                    >
                      {t('previous')}
                    </Button>
                  </PaginationItem>
                  
                  {/* Smart pagination with ellipsis */}
                  {(() => {
                    const pages: (number | string)[] = [];
                    const showEllipsisStart = currentPage > 3;
                    const showEllipsisEnd = currentPage < totalPages - 2;
                    
                    // Always show first page
                    pages.push(1);
                    
                    if (showEllipsisStart) {
                      pages.push('ellipsis-start');
                    }
                    
                    // Show pages around current
                    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                      if (!pages.includes(i)) {
                        pages.push(i);
                      }
                    }
                    
                    if (showEllipsisEnd) {
                      pages.push('ellipsis-end');
                    }
                    
                    // Always show last page
                    if (totalPages > 1 && !pages.includes(totalPages)) {
                      pages.push(totalPages);
                    }
                    
                    return pages.map((page, idx) => {
                      if (typeof page === 'string') {
                        return (
                          <PaginationItem key={page + idx}>
                            <span className="px-2 text-muted-foreground">...</span>
                          </PaginationItem>
                        );
                      }
                      
                      return (
                        <PaginationItem key={page}>
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={`h-9 w-9 p-0 ${currentPage === page ? 'bg-primary text-primary-foreground' : ''}`}
                          >
                            {page}
                          </Button>
                        </PaginationItem>
                      );
                    });
                  })()}
  
                  {/* Next button */}
                  <PaginationItem>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="h-9 px-3"
                    >
                      {t('next')}
                    </Button>
                  </PaginationItem>
                                  </PaginationContent>
                                </Pagination>
                              </div>
                            </div>
                          </div>
                        </main>
                        <Footer />
                      </div>
                    );
                };      
      export default IndustryDetail;  
